import { useCallback, useEffect, useMemo, useState, type FormEvent, type JSX } from 'react'
import {
  ApiError,
  api,
  can,
  codeWords,
  refusalReason,
  shortDate,
  valueLabel,
  type CatalogueValue,
  type DataElementCatalogue,
  type DataElementDef,
  type DataProcessing,
  type DataProcessingWrite,
  type Issue,
  type ResidencyView,
  type VendorDocument,
} from '../lib/api'
import { ErrorBox, Loading } from './DataTable'
import { Dialog, Status, Toggletip, Picker } from './kit'
import { ReasonTip } from './vendor/ReasonTip'
import { ErrorReferenceOf } from './ui'

// What kinds of data this vendor processes.
//
// # KINDS OF DATA, NEVER THE DATA
//
// Every control here records a category. Ticking Aadhaar says the vendor
// handles Aadhaar numbers; there is nowhere on this screen to put one, and the
// note under each chosen element says so where somebody might be tempted. A
// register that accumulated the data it measures would be the second copy an
// incident is about, which is why the sentence at the top of the panel is not
// decoration.
//
// # WHY THE REASONS ARE FETCHED AND NOT WRITTEN HERE
//
// The reason beside each element, each classification and each subject count
// band comes from GET /v1/data-elements, which serves what packages/core/domain
// holds and migration 0028 enforces as a CHECK. Choosing Aadhaar is a different
// act from choosing an email address and the screen has to say so at the moment
// of choosing, in the catalogue's words, so there is one set of words to keep
// true rather than two.
//
// The catalogue is served under config.read and the record under vendor.read,
// which are different capabilities. So the catalogue load is allowed to fail:
// the record still renders from what it carries, and the chooser says why it is
// not there. A screen that refused to show a record because a reference list
// was out of reach would be refusing the more useful half.
//
// # WHAT THIS DERIVES AND WHAT IT ASKS
//
// The vendor's classification is derived and never typed: it is the highest of
// the chosen elements, taken in the order the catalogue serves them. The API
// derives the same value on write by the same rule. The screen shows the
// derivation next to the answer so nobody has to wonder where Restricted came
// from.
//
// # THE CROSS-BORDER PANEL HAS TWO SOURCES AND SAYS WHICH
//
// After a save, GET /v1/vendors/{id}/residency is the answer: it reads the
// vendor, this record and the sub-processor register together and returns the
// flag, its reasons and the eight things section 13 asks for. While somebody is
// typing, no such answer exists yet, so the form shows a preview derived from
// the draft and labels it as one.

// India, exactly as the API spells it. The residency view compares against IN
// and the write refuses anything that is not two upper case letters, so a
// friendlier reading here (INDIA, IND) would put the panel on this screen at
// odds with the panel the API serves for the same vendor.
const INDIA = 'IN'

function outsideIndia(country: string | undefined | null): boolean {
  const c = (country ?? '').trim().toUpperCase()
  return c !== '' && c !== INDIA
}

const ALPHA2 = /^[A-Z]{2}$/

// The API refuses anything longer than this: a century is not a retention
// policy, it is years typed where months belong.
const ABSURD_RETENTION_MONTHS = 1200

// The three groups the chooser splits into, in the order a person reads them:
// what is about a person, what is about a person and reportable when it leaks,
// then everything else. Which group an element belongs to is the catalogue's
// answer, read off its own two flags. The detail sits behind the (i) beside the
// group's title rather than as a paragraph above the boxes.
type Group = { key: string; title: string; detail: string; holds: (e: DataElementDef) => boolean }

const GROUPS: Group[] = [
  {
    key: 'personal',
    title: 'Personal data',
    detail:
      'About an identifiable individual. Choosing any of these makes this organisation answerable for what the vendor holds, not only for what it does.',
    holds: (e) => e.personal && !e.sensitive,
  },
  {
    key: 'sensitive',
    title: 'Sensitive personal data',
    detail:
      'The catalogue marks these sensitive. Read the reason on each before ticking it: a breach here is reportable, and two of them cannot be reissued to the person afterwards.',
    holds: (e) => e.sensitive,
  },
  {
    key: 'other',
    title: 'Everything else',
    detail: 'Not about an identifiable person. Still classified, because a ledger is not public.',
    holds: (e) => !e.personal && !e.sensitive,
  },
]

type ElementDraft = { classification: string; note: string }

type Draft = {
  // Keyed by element code. A key present is an element chosen.
  elements: Record<string, ElementDraft>
  subjectCountBand: string
  processingPurpose: string
  retentionPeriodMonths: string
  retentionBasis: string
  storageCountry: string
  processingCountry: string
  transferCountries: string[]
  deletionMechanism: string
  deletionEvidenceDocumentId: string
}

const EMPTY_DRAFT: Draft = {
  elements: {},
  subjectCountBand: '',
  processingPurpose: '',
  retentionPeriodMonths: '',
  retentionBasis: '',
  storageCountry: '',
  processingCountry: '',
  transferCountries: [],
  deletionMechanism: '',
  deletionEvidenceDocumentId: '',
}

function draftFrom(record: DataProcessing | null): Draft {
  if (!record) return { ...EMPTY_DRAFT, elements: {} }
  const elements: Record<string, ElementDraft> = {}
  for (const e of record.elements) {
    elements[e.elementCode] = { classification: e.classification, note: e.note ?? '' }
  }
  return {
    elements,
    subjectCountBand: record.subjectCountBand ?? '',
    processingPurpose: record.processingPurpose ?? '',
    retentionPeriodMonths:
      record.retentionPeriodMonths === null || record.retentionPeriodMonths === undefined
        ? ''
        : String(record.retentionPeriodMonths),
    retentionBasis: record.retentionBasis ?? '',
    storageCountry: record.storageCountry ?? '',
    processingCountry: record.processingCountry ?? '',
    transferCountries: [...record.transferCountries],
    deletionMechanism: record.deletionMechanism ?? '',
    deletionEvidenceDocumentId: record.deletionEvidenceDocumentId ?? '',
  }
}

// A classification's rank is its position in the catalogue, because the
// catalogue is served least exposed first and that order is the ranking. An
// unrecognised code ranks below everything, which is what the API does with one.
function rankOf(order: CatalogueValue[], code: string): number {
  return order.findIndex((c) => c.code === code)
}

// The vendor's classification: the highest of its elements, by the rule the API
// applies on write. Shown here so the answer on the screen is the answer that
// will be stored.
function highestOf(order: CatalogueValue[], codes: string[]): string {
  let best = -1
  for (const code of codes) {
    const at = rankOf(order, code)
    if (at > best) best = at
  }
  return best < 0 ? '' : (order[best]?.code ?? '')
}

// The refusals the API makes on this body, made here first so nobody spends a
// round trip learning something the form already knew. Every one of them
// mirrors a rule in validateDataProcessing, in this screen's words rather than
// the API's; where the API refuses anyway, its wording wins, because it is the
// one that ran against the request as it arrived.
function validate(draft: Draft, catalogue: DataElementCatalogue): Issue[] {
  const issues: Issue[] = []
  const chosen = catalogue.dataElements.filter((e) => draft.elements[e.code] !== undefined)

  if (draft.subjectCountBand === '') {
    issues.push({
      field: 'subjectCountBand',
      code: 'REQUIRED',
      detail: 'Say how many people this is about. No individuals is an answer, a blank is not.',
    })
  } else if (draft.subjectCountBand === 'NONE' && chosen.some((e) => e.personal)) {
    issues.push({
      field: 'subjectCountBand',
      code: 'INCONSISTENT',
      detail: 'Personal data is ticked but the band says nobody. One of the two is wrong.',
    })
  }

  for (const element of chosen) {
    if ((draft.elements[element.code]?.classification ?? '') === '') {
      issues.push({
        field: 'elements.' + element.code,
        code: 'REQUIRED',
        detail: 'Classify this element, or the vendor classification reads lower than it is.',
      })
    }
  }

  const retention = draft.retentionPeriodMonths.trim()
  if (retention !== '' && !/^\d+$/.test(retention)) {
    issues.push({
      field: 'retentionPeriodMonths',
      code: 'INVALID',
      detail: 'Whole months, not negative. Leave it empty if no period is agreed.',
    })
  } else if (retention !== '' && Number(retention) > ABSURD_RETENTION_MONTHS) {
    issues.push({
      field: 'retentionPeriodMonths',
      code: 'INVALID',
      detail: 'That is over a hundred years. Months belong here, not years.',
    })
  }

  for (const c of [
    { field: 'storageCountry', value: draft.storageCountry },
    { field: 'processingCountry', value: draft.processingCountry },
  ]) {
    const value = c.value.trim().toUpperCase()
    if (value !== '' && !ALPHA2.test(value)) {
      issues.push({
        field: c.field,
        code: 'INVALID',
        detail: value + ' is not a two letter country code. India is IN and Singapore is SG.',
      })
    }
  }

  for (const c of draft.transferCountries) {
    if (!ALPHA2.test(c)) {
      issues.push({ field: 'transferCountries', code: 'INVALID', detail: c + ' is not a two letter country code.' })
      continue
    }
    if (draft.storageCountry.trim().toUpperCase() === c) {
      issues.push({
        field: 'transferCountries',
        code: 'REDUNDANT',
        detail: c + ' is the storage country, so it is not a transfer country.',
      })
    }
  }

  return issues
}

// An issue named for a field, or for one entry of it. The API numbers the
// entries of a list, as in transferCountries[1], and both belong at the same
// control.
function issuesAt(issues: Issue[], field: string): Issue[] {
  return issues.filter((i) => i.field === field || i.field.startsWith(field + '['))
}

function elementIssues(issues: Issue[], code: string): Issue[] {
  const at = 'elements.' + code
  return issues.filter((i) => i.field === at || i.field.startsWith(at + '.'))
}

// The API names an element issue by its position in the array it was sent,
// elements[2].classification, because that is what it has. The form knows what
// went into position 2, so the position is turned back into the element before
// anything is rendered; otherwise every element issue lands nowhere and the
// person is left with a refusal and no field to look at.
function relabelElementIssues(issues: Issue[], codes: string[]): Issue[] {
  return issues.map((i) => {
    const found = /^elements\[(\d+)\]/.exec(i.field)
    if (!found) return i
    const code = codes[Number(found[1])]
    if (!code) return i
    return { ...i, field: 'elements.' + code + i.field.slice(found[0].length) }
  })
}

// Where a value on the record came from when a contract supplied it (release
// 3.3, D5): the document and the clause, as the API keeps them beside the
// element or the field (migration 0113). A value typed on this screen has no
// source and shows none. A reading a person corrected is their assertion, with
// the clause it corrected. The record does not keep the person's note; the
// Documents part shows it beside the decision. A list the contract only added
// to (partial) says some of it came from the contract, never all of it. The
// place is blank for a reader the API withholds a restricted document from;
// the source is still said.
type Sourced = { source?: string; ref?: string; partial?: boolean }
type FieldSources = { fieldSources?: Record<string, Sourced> }
type ElementSource = { source?: string; sourceRef?: string }

export function sourceLine(s: Sourced | undefined): string {
  const ref = (s?.ref ?? '').trim()
  if (s?.source === 'DOCUMENT_EXTRACTED') {
    const lead = s.partial ? 'Some came from the contract' : 'From the contract'
    return ref === '' ? lead : lead + ': ' + ref
  }
  if (s?.source === 'INTERNAL_ASSERTED') {
    return (s.partial ? 'Some are an internal assertion: corrected from ' : 'Internal assertion: corrected from ') +
      (ref === '' ? 'the contract' : ref)
  }
  return ''
}

function SourceLine({ of }: { of: Sourced | undefined }): JSX.Element | null {
  const line = sourceLine(of)
  return line ? <span className="cell-sub">{line}</span> : null
}

// A list read as a sentence rather than as a comma-separated dump, because both
// places this is used are sentences somebody reads out loud.
function sentenceList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? ''
  return items.slice(0, -1).join(', ') + ' and ' + (items[items.length - 1] ?? '')
}

function asApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))
}

function FieldIssues({ issues, id }: { issues: Issue[]; id: string }): JSX.Element | null {
  if (issues.length === 0) return null
  return (
    <div className="field-issue" id={id} role="alert">
      {issues.map((i) => (
        <p key={i.code + i.detail} className="field-issue-line">
          <span className="field-issue-code">{i.code.replace(/_/g, ' ').toLowerCase()}</span> {i.detail}
        </p>
      ))}
    </div>
  )
}

// A 403 is two different walls and they need different sentences. FORBIDDEN is
// the capability check: the sign-in does not hold the act. Anything else is the
// ownership rule, which refuses a person who holds the act for a vendor that is
// not theirs, and the API's own message names the owner.
function Refusal({ error, onDismiss }: { error: ApiError; onDismiss: () => void }): JSX.Element {
  let heading = 'That did not go through'
  let body = error.message
  let next = error.remedy

  if (error.status === 403 && error.code === 'FORBIDDEN') {
    heading = 'Your role cannot record what a vendor processes'
    body = 'Reading a vendor and changing its tier facts are separate permissions.'
    next = refusalReason('vendor.edit')
  } else if (error.status === 403) {
    heading = 'This vendor is not yours to change'
  } else if (error.status === 409 && error.code === 'RECORD_CHANGED') {
    heading = 'The record changed after you opened it'
  } else if (error.status === 422 && error.code === 'ACKNOWLEDGEMENT_REQUIRED') {
    heading = 'Nothing here is wrong, and it is worth a second look'
    body = error.message || 'Confirm the classification below and your name is recorded against it.'
  } else if (error.status === 422 && error.issues.length > 0) {
    heading = 'Refused'
    body = 'The issues are marked against the fields below.'
  }

  return (
    <div className="note stop decision-note" role="alert">
      <strong>{heading}</strong>
      <p>{body}</p>
      <p className="muted">{next}</p>
      {/* The code and the reference support looks the request up by
          (release 3.1); shown here, so the feedback net adds no toast. */}
      <ErrorReferenceOf error={error} />
      <button type="button" onClick={onDismiss}>
        Dismiss
      </button>
    </div>
  )
}

// What the record does not say, named rather than counted: "no retention
// period" is something a reviewer can act on and a completeness percentage is
// not.
function whatIsMissing(record: DataProcessing): string[] {
  const gaps: string[] = []
  if (record.elements.length === 0) gaps.push('elements')
  if (!record.processingPurpose) gaps.push('purpose')
  if (record.retentionPeriodMonths === null || record.retentionPeriodMonths === undefined) {
    gaps.push('retention')
  }
  if (!record.storageCountry) gaps.push('storage country')
  if (!record.processingCountry) gaps.push('processing country')
  if (!record.deletionMechanism) gaps.push('deletion')
  return gaps
}

// The gate the shipped model carries, stated where somebody is deciding whether
// to record a country. It is named as the shipped model's rule rather than as a
// fact about this tenant, because a tenant can edit its model and the score
// steps on the assessment are what say which gates actually fired.
function CapNote(): JSX.Element {
  const [open, setOpen] = useState(false)
  return (
    <p className="note warn vd-inline">
      <Status tone="warn">Capped at High</Status>
      <strong>Until DP-04 is answered</strong>
      <button type="button" className="linkish" onClick={() => setOpen(true)}>
        About this cap
      </button>
      <Dialog shape="drawer" kind="record" open={open} onClose={() => setOpen(false)} title="Why the band is capped at High">
        <p className="vd-reason">
          The risk model that ships with the product caps the band at High for a vendor recorded as processing personal
          data outside India while DP-04 is outstanding, citing section 16 of the DPDP Act 2023 read with Rule 14 of the
          DPDP Rules 2025.
        </p>
        <p className="vd-reason">
          It is a cap on what we know rather than a finding about the vendor, and it lifts the moment the question is
          answered either way. A tenant that has edited its model may do something else, and the score steps on the
          assessment say which gates fired.
        </p>
      </Dialog>
    </p>
  )
}

// Section 13 as the API answers it: the flag, why it is raised, and the eight
// things the requirement asks for with the place each answer lives.
//
// Why the flag is raised is the API's reasoning, and it opens in a side panel
// from one line rather than sitting as a list above the questions: the review
// of 17 September asked for reasoning of this kind to leave the working screen.
function ResidencyPanel({ view, personal }: { view: ResidencyView; personal: boolean }): JSX.Element | null {
  const [why, setWhy] = useState(false)
  const raised = view.flag?.raised === true
  const reasons = view.flag?.reasons ?? []
  const questions = view.additionalQuestions ?? []
  if (!raised && reasons.length === 0) return null

  return (
    <section className="panel" aria-labelledby="dp-cross-heading">
      <h2 id="dp-cross-heading">{raised ? (view.flag?.label ?? 'Cross-border data processing') : 'Data residency'}</h2>

      {reasons.length > 0 && (
        <p style={{ margin: '0 0 10px' }}>
          <button type="button" className="linkish" onClick={() => setWhy(true)}>
            {reasons.length === 1 ? 'Why this is raised' : 'Why this is raised (' + reasons.length + ' reasons)'}
          </button>
        </p>
      )}
      <Dialog shape="drawer" kind="record" open={why} onClose={() => setWhy(false)} title="Why this is raised">
        <ul className="rule-list">
          {reasons.map((r) => (
            <li key={r.code + r.detail}>
              <strong>{codeWords(r.code)}</strong>
              <p className="vd-reason">{r.detail}</p>
            </li>
          ))}
        </ul>
      </Dialog>

      {questions.length > 0 && (
        <>
          <p className="label-block" id="dp-cross-asks">
            What section 13 asks for on top of the record
          </p>
          <dl className="kv" aria-labelledby="dp-cross-asks" style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' }}>
            {questions.map((q) => (
              <div key={q.key} style={{ display: 'contents' }}>
                <dt style={{ fontWeight: 400 }}>{q.question}</dt>
                <dd>
                  {/* Where the answer lives is the product's own
                      bookkeeping, so it sits behind a hover beside the
                      answer rather than as a paragraph under it. */}
                  {q.answered ? (
                    <span className="mono">{q.answer}</span>
                  ) : (
                    <span className="muted">not answered here</span>
                  )}{' '}
                  <ReasonTip hover label={'Where this is held: ' + q.question.replace(/\?$/, '')} text={whereHeld(q.heldIn)} />
                </dd>
              </div>
            ))}
          </dl>
        </>
      )}

      {raised && personal && <CapNote />}

      {view.note !== undefined && view.note !== '' && (
        <p className="vd-inline vd-quiet">
          About this view
          <ReasonTip label="About this view" text={view.note} />
        </p>
      )}
    </section>
  )
}

// The API writes heldIn as a fragment that follows "held in": the vendor
// record, the agreement, the sub-processor register. Two of the eight say
// instead that the product does not hold the answer at all, and those read as
// sentences of their own rather than as the tail of one.
function whereHeld(heldIn: string): string {
  if (heldIn.startsWith('not ')) return heldIn.charAt(0).toUpperCase() + heldIn.slice(1)
  return 'Held in ' + heldIn
}

type PreviewFacts = {
  storageCountry: string
  processingCountry: string
  transferCountries: string[]
  sensitiveChosen: boolean
  personalChosen: boolean
}

// What the residency view will say once this is saved, derived from the draft.
// It exists because the API's answer is about the record that stands, and
// somebody typing a country is entitled to know what it means before they
// commit to it.
function CrossBorderPreview({ facts, saved }: { facts: PreviewFacts; saved: boolean }): JSX.Element | null {
  const abroadStorage = outsideIndia(facts.storageCountry)
  const abroadProcessing = outsideIndia(facts.processingCountry)
  const abroadTransfers = facts.transferCountries.filter((c) => outsideIndia(c))
  if (!abroadStorage && !abroadProcessing && abroadTransfers.length === 0) return null

  return (
    <section className="panel" aria-labelledby="dp-preview-heading">
      <h2 id="dp-preview-heading">Cross-border processing</h2>
      <p className="field-note">
        {saved ? 'From this record, because the full section 13 view could not be read' : 'A preview of this record once it is saved'}
      </p>

      <ul className="rule-list">
        {abroadStorage && (
          <li>
            Stored in <span className="mono">{facts.storageCountry.trim().toUpperCase()}</span>.
          </li>
        )}
        {abroadProcessing && (
          <li>
            Processed in <span className="mono">{facts.processingCountry.trim().toUpperCase()}</span>.
          </li>
        )}
        {abroadTransfers.length > 0 && (
          <li>
            Transferred to <span className="mono">{abroadTransfers.join(', ')}</span>.
          </li>
        )}
        <li className="vd-inline">
          Safeguards are asked at <span className="mono">DP-04</span>
          <Toggletip label="About transfer safeguards">
            The transfer mechanism and its safeguard are asked of the vendor at DP-04, and the sub-processors in that country are named in the table below.
          </Toggletip>
        </li>
      </ul>

      {facts.personalChosen && <CapNote />}

    </section>
  )
}

export function VendorDataProcessing({
  vendorId,
  refreshKey,
  onSaved,
}: {
  vendorId: string
  // Bumped by the sub-processor table beside this one. Naming a party in
  // Germany changes the residency answer above it, and a panel that kept
  // yesterday's answer while the register moved underneath would be the kind of
  // stale that nobody notices.
  refreshKey?: number
  onSaved?: () => void
}): JSX.Element {
  const [catalogue, setCatalogue] = useState<DataElementCatalogue | null>(null)
  const [catalogueError, setCatalogueError] = useState<ApiError | null>(null)
  const [record, setRecord] = useState<DataProcessing | null>(null)
  const [apiNote, setApiNote] = useState('')
  const [residency, setResidency] = useState<ResidencyView | null>(null)
  // Null means two things and the panel below says different sentences for
  // them: nobody has answered the read yet, and the read failed. Without this
  // flag the in-flight case renders the fallback and asserts a failure that has
  // not happened.
  const [residencyFailed, setResidencyFailed] = useState(false)
  const [documents, setDocuments] = useState<VendorDocument[]>([])
  const [error, setError] = useState<unknown>(null)
  const [loaded, setLoaded] = useState(false)

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT)
  const [issues, setIssues] = useState<Issue[]>([])
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<ApiError | null>(null)
  const [outcome, setOutcome] = useState('')
  const [acknowledged, setAcknowledged] = useState(false)
  const [ackMissed, setAckMissed] = useState(false)
  const [transferDraft, setTransferDraft] = useState('')
  // The record the form was opened on, so a save never lands on a record
  // that changed since (a contract reading accepted in the panel above).
  const [basedOn, setBasedOn] = useState('')

  const load = useCallback(() => {
    setLoaded(false)
    setResidencyFailed(false)
    api
      .dataProcessing(vendorId)
      .then((reply) => {
        setRecord(reply.record)
        setApiNote(reply.note)
        setError(null)
        setLoaded(true)
      })
      .catch((err) => {
        setError(err)
        setLoaded(true)
      })
    // The catalogue is configuration and the record is vendor data, held under
    // different capabilities. Asking for it only where it will be answered
    // saves a 403 that would tell the person nothing they can act on.
    if (can('config.read')) {
      api
        .dataElementCatalogue()
        .then((cat) => {
          setCatalogue(cat)
          setCatalogueError(null)
        })
        .catch((err) => setCatalogueError(asApiError(err)))
    }
    // The residency view and the document list are both worth having and
    // neither is worth failing the screen over: one is a reading of facts the
    // record already carries, the other fills an optional select.
    api
      .residency(vendorId)
      .then((view) => {
        setResidency(view)
        setResidencyFailed(false)
      })
      .catch(() => {
        setResidency(null)
        setResidencyFailed(true)
      })
    api
      .vendorDocuments(vendorId)
      .then((r) => setDocuments(r.documents))
      .catch(() => setDocuments([]))
  }, [vendorId, refreshKey])

  useEffect(load, [load])

  const classifications = catalogue?.classifications ?? []

  // Sensitive elements classified below Restricted. It is a legitimate answer
  // and an unusual one, so it is acknowledged rather than blocked: the API
  // refuses the write once, asks for acknowledge, and records the
  // acknowledgement on the chain against whoever gave it.
  const understated = useMemo(() => {
    if (!catalogue) return []
    const restricted = rankOf(classifications, 'RESTRICTED')
    if (restricted < 0) return []
    return catalogue.dataElements
      .filter((e) => e.sensitive)
      .map((e) => ({ element: e, chosen: draft.elements[e.code] }))
      .filter((row) => row.chosen !== undefined && row.chosen.classification !== '')
      .filter((row) => rankOf(classifications, row.chosen?.classification ?? '') < restricted)
      .map((row) => ({ code: row.element.code, name: row.element.name, level: row.chosen?.classification ?? '' }))
  }, [catalogue, classifications, draft.elements])

  const understatedKey = understated.map((u) => u.code + ':' + u.level).join('|')

  useEffect(() => {
    // A changed set is a changed acknowledgement. Ticking the box for Aadhaar
    // at Internal must not carry over to biometrics at Confidential.
    setAcknowledged(false)
    setAckMissed(false)
  }, [understatedKey])

  if (error != null) return <ErrorBox error={error} what="What this vendor processes" />
  if (!loaded) return <Loading what="what this vendor processes" />

  const cat = catalogue
  const canEdit = can('vendor.edit') && cat !== null
  const chosenCodes = Object.keys(draft.elements)
  const derived = highestOf(
    classifications,
    chosenCodes.map((c) => draft.elements[c]?.classification ?? ''),
  )
  const derivedRow = classifications.find((c) => c.code === derived)
  const recordedRow = record ? classifications.find((c) => c.code === record.classification) : undefined
  const bandRow = cat?.subjectCountBands.find((b) => b.code === draft.subjectCountBand)
  const sensitiveChosen = (cat?.dataElements ?? []).some((e) => e.sensitive && draft.elements[e.code] !== undefined)
  const personalChosen = (cat?.dataElements ?? []).some((e) => e.personal && draft.elements[e.code] !== undefined)
  const held = record
  const recordSensitive = held
    ? (cat?.dataElements ?? []).some((e) => e.sensitive && held.elements.some((r) => r.elementCode === e.code))
    : false
  // Without the catalogue the personal flags are unknown, and a record with any
  // element on it is treated as personal for the cap note: over-saying it is
  // the safe direction, and the API's own residency view is the authority.
  const recordPersonal = held
    ? cat === null
      ? held.elements.length > 0
      : cat.dataElements.some((e) => e.personal && held.elements.some((r) => r.elementCode === e.code))
    : false

  function begin() {
    setDraft(draftFrom(record))
    setBasedOn(record?.id ?? '')
    setIssues([])
    setSaveError(null)
    setOutcome('')
    setTransferDraft('')
    // Opening the form is a new decision, so an acknowledgement made in the
    // last one does not carry into it. Reopening on an unchanged record leaves
    // the set identical, which is the one case the effect above cannot catch.
    setAcknowledged(false)
    setAckMissed(false)
    setEditing(true)
  }

  function setElement(code: string, next: ElementDraft | null) {
    setDraft((d) => {
      const elements = { ...d.elements }
      if (next === null) delete elements[code]
      else elements[code] = next
      return { ...d, elements }
    })
  }

  function addTransferCountry() {
    const value = transferDraft.trim().toUpperCase()
    if (value === '') return
    setDraft((d) =>
      d.transferCountries.includes(value) ? d : { ...d, transferCountries: [...d.transferCountries, value] },
    )
    setTransferDraft('')
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!cat) return
    // A country typed into the box and never committed with Enter or the add
    // button is still what the person meant to record. Dropping it and then
    // saying the record was written was the screen disagreeing with itself, so
    // it is folded in before anything is validated or sent, and put back on the
    // screen so the chips match the body.
    const pending = transferDraft.trim().toUpperCase()
    const ready: Draft =
      pending === '' || draft.transferCountries.includes(pending)
        ? draft
        : { ...draft, transferCountries: [...draft.transferCountries, pending] }
    if (ready !== draft) {
      setDraft(ready)
      setTransferDraft('')
    }

    const found = validate(ready, cat)
    setIssues(found)
    if (understated.length > 0 && !acknowledged) {
      setAckMissed(true)
      return
    }
    if (found.length > 0) return

    // In catalogue order rather than click order, so two people recording the
    // same eight elements send the same body.
    const elements = cat.dataElements
      .filter((e) => ready.elements[e.code] !== undefined)
      .map((e) => ({
        elementCode: e.code,
        classification: ready.elements[e.code]?.classification ?? '',
        note: (ready.elements[e.code]?.note ?? '').trim(),
      }))
    const codes = elements.map((e) => e.elementCode)

    const retention = ready.retentionPeriodMonths.trim()
    const body: DataProcessingWrite = {
      acknowledge: acknowledged,
      subjectCountBand: ready.subjectCountBand,
      processingPurpose: ready.processingPurpose.trim(),
      retentionPeriodMonths: retention === '' ? null : Number(retention),
      retentionBasis: ready.retentionBasis.trim(),
      storageCountry: ready.storageCountry.trim().toUpperCase(),
      processingCountry: ready.processingCountry.trim().toUpperCase(),
      transferCountries: ready.transferCountries,
      deletionMechanism: ready.deletionMechanism.trim(),
      deletionEvidenceDocumentId: ready.deletionEvidenceDocumentId,
      elements,
      basedOnRecordId: basedOn,
    }

    setSaving(true)
    setSaveError(null)
    api
      .putDataProcessing(vendorId, body)
      .then((reply) => {
        setRecord(reply.record)
        setApiNote(reply.note)
        setEditing(false)
        const level = reply.record?.classification ?? derived
        const named = classifications.find((c) => c.code === level)
        // Two short sentences at most. How the level was reached is on the (i)
        // beside it, and the tier's own panel says which rule decided it.
        const moved =
          reply.tierChanged === true
            ? ' Tier moved from ' + (reply.previousTierLabel || 'no tier') + ' to ' + (reply.decision?.label || 'its new tier') + '.'
            : ''
        setOutcome('Saved as ' + (named ? valueLabel(named) : level || 'nothing yet') + '.' + moved)
        if (onSaved) onSaved()
        api
          .residency(vendorId)
          .then((view) => {
            setResidency(view)
            setResidencyFailed(false)
          })
          .catch(() => setResidencyFailed(true))
      })
      .catch((err) => {
        const failed = asApiError(err)
        setSaveError(failed)
        if (failed.issues.length > 0) setIssues(relabelElementIssues(failed.issues, codes))
        if (failed.code === 'ACKNOWLEDGEMENT_REQUIRED') setAckMissed(true)
      })
      .finally(() => setSaving(false))
  }

  return (
    <>

      {outcome && (
        <p className="ok-note" role="status">
          {outcome}
        </p>
      )}

      {!editing && (
        <section className="panel" aria-labelledby="dp-heading">
          <div className="panel-head" style={{ background: 'transparent', border: 0, padding: 0 }}>
            <h2 id="dp-heading" style={{ margin: 0 }}>
              What this vendor processes
            </h2>
            <button
              type="button"
              onClick={begin}
              disabled={!canEdit}
              title={can('vendor.edit') ? undefined : refusalReason('vendor.edit')}
            >
              {held ? 'Change this record' : 'Record what it processes'}
            </button>
          </div>

          {!held ? (
            <div className="empty" style={{ marginTop: 12 }}>
              <strong>Not recorded yet</strong>
              <span className="vd-inline">
                The tier rules read this before any questionnaire
                <Toggletip label="Why this record matters">
                  The privacy and information security assessments run on these facts. Until a record exists, this vendor is tiered on its service and access alone.
                </Toggletip>
              </span>
            </div>
          ) : (
            <>
              <p className="dp-verdict" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {recordedRow ? valueLabel(recordedRow) : held.classification}
                <ReasonTip
                  hover
                  label="About this classification"
                  text={
                    'The highest of the ' +
                    held.elements.length +
                    ' element' +
                    (held.elements.length === 1 ? '' : 's') +
                    ' below.' +
                    (recordedRow?.why ? ' ' + recordedRow.why : '')
                  }
                />
              </p>

              <div className="chiprow" style={{ margin: '10px 0 14px', alignItems: 'flex-start' }}>
                {held.elements.length === 0 ? (
                  <span className="muted">No element is on this record.</span>
                ) : (
                  held.elements.map((e) => {
                    const def = cat?.dataElements.find((d) => d.code === e.elementCode)
                    const name = e.elementName || def?.name || e.elementCode
                    const from = e as typeof e & ElementSource
                    return (
                      // The elements only, as the review of 17 September
                      // asked. The note stays for Other sensitive data alone,
                      // behind a hover, because there it is the only thing that
                      // says what the element is at all. An element a contract
                      // supplied says which document and clause, under it.
                      <span key={e.elementCode} style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
                        <span className="vd-inline" style={{ gap: 4 }}>
                          <span className={def?.sensitive ? 'pill pill-pii' : 'pill'}>
                            {name}: {e.classification.toLowerCase()}
                          </span>
                          {e.elementCode === 'OTHER_SENSITIVE' && e.note && (
                            <ReasonTip hover label={'What ' + name.toLowerCase() + ' is'} text={e.note} />
                          )}
                        </span>
                        <SourceLine of={{ source: from.source, ref: from.sourceRef }} />
                      </span>
                    )
                  })
                )}
              </div>

              <dl className="kv" style={{ gridTemplateColumns: '170px minmax(0, 1fr)' }}>
                <dt>Data subjects</dt>
                <dd>
                  {held.subjectCountBand ? (
                    <span className="vd-inline">
                      {valueLabel(
                        cat?.subjectCountBands.find((b) => b.code === held.subjectCountBand) ?? {
                          code: held.subjectCountBand,
                          why: '',
                        },
                      )}
                      <ReasonTip
                        label="About this band"
                        text={cat?.subjectCountBands.find((b) => b.code === held.subjectCountBand)?.why}
                      />
                    </span>
                  ) : (
                    <span className="muted">not recorded</span>
                  )}
                </dd>
                <dt>Purpose</dt>
                <dd>{held.processingPurpose || <span className="muted">not recorded</span>}</dd>
                <dt>Retention</dt>
                <dd>
                  {held.retentionPeriodMonths === null || held.retentionPeriodMonths === undefined ? (
                    <span className="muted">no period agreed</span>
                  ) : (
                    <>
                      <span className="mono">{held.retentionPeriodMonths}</span> months
                    </>
                  )}
                  {held.retentionBasis && <span className="cell-sub">{held.retentionBasis}</span>}
                  <SourceLine of={(held as DataProcessing & FieldSources).fieldSources?.retentionPeriodMonths} />
                </dd>
                <dt>Stored in</dt>
                <dd className="mono">{held.storageCountry || <span className="muted">not recorded</span>}</dd>
                <dt>Processed in</dt>
                <dd className="mono">{held.processingCountry || <span className="muted">not recorded</span>}</dd>
                <dt>Transferred to</dt>
                <dd className="mono">
                  {held.transferCountries.length > 0 ? (
                    held.transferCountries.join(', ')
                  ) : (
                    <span className="muted">nowhere recorded</span>
                  )}
                  <SourceLine of={(held as DataProcessing & FieldSources).fieldSources?.transferCountries} />
                </dd>
                <dt>Deleted by</dt>
                <dd>
                  {held.deletionMechanism || <span className="muted">not recorded</span>}
                  <SourceLine of={(held as DataProcessing & FieldSources).fieldSources?.deletionMechanism} />
                  {held.deletionEvidenceDocumentId && (
                    <span className="cell-sub">
                      Evidence:{' '}
                      {documents.find((d) => d.id === held.deletionEvidenceDocumentId)?.title ??
                        held.deletionEvidenceDocumentId}
                    </span>
                  )}
                </dd>
                <dt>Recorded</dt>
                <dd>
                  <span className="mono">{shortDate(held.recordedAt)}</span>
                  {held.recordedBy && <span className="cell-sub mono">{held.recordedBy}</span>}
                </dd>
              </dl>

              {whatIsMissing(held).length > 0 && (
                <p className="field-note vd-inline">
                  <Status tone="warn">Not recorded</Status>
                  {sentenceList(whatIsMissing(held))}
                  <Toggletip label="Why the gaps matter">Each one is a question an assessment or an incident asks early.</Toggletip>
                </p>
              )}

              {apiNote && (
                <p className="vd-inline vd-quiet">
                  About this record
                  <ReasonTip label="About this record" text={apiNote} />
                </p>
              )}
            </>
          )}

          {catalogueError !== null && (
            <p className="self-note vd-inline">
              Catalogue not available ({catalogueError.code})
              <Toggletip label="About the catalogue">
                The reasons behind each element are not shown and this record cannot be changed here. The catalogue needs a different permission from reading this vendor.
              </Toggletip>
            </p>
          )}
          {catalogueError === null && !can('config.read') && (
            <p className="self-note vd-inline">
              Changing this needs the catalogue permission
              <ReasonTip label="Why this cannot be changed" text={refusalReason('config.read')} />
            </p>
          )}
          {!can('vendor.edit') && (
            <p className="self-note vd-inline">
              Read only
              <ReasonTip label="Why read only" text={refusalReason('vendor.edit')} />
            </p>
          )}
        </section>
      )}

      {!editing && residency !== null && <ResidencyPanel view={residency} personal={recordPersonal} />}
      {!editing && residency === null && residencyFailed && held && (
        <CrossBorderPreview
          facts={{
            storageCountry: held.storageCountry ?? '',
            processingCountry: held.processingCountry ?? '',
            transferCountries: held.transferCountries,
            sensitiveChosen: recordSensitive,
            personalChosen: recordPersonal,
          }}
          saved
        />
      )}

      {editing && cat !== null && (
        <form onSubmit={submit} noValidate>
          {saveError !== null && <Refusal error={saveError} onDismiss={() => setSaveError(null)} />}

          <fieldset className="block">
            <legend>What kinds of data</legend>

            <p className="label-block" style={{ marginTop: 10 }}>
              Vendor classification
            </p>
            <p className={derivedRow ? 'dp-verdict' : 'dp-verdict muted'} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {derivedRow ? valueLabel(derivedRow) : 'Nothing chosen yet'}
              <ReasonTip
                hover
                label="About this classification"
                text={'The highest of the elements you tick, never typed.' + (derivedRow?.why ? ' ' + derivedRow.why : '')}
              />
            </p>

            <FieldIssues issues={issuesAt(issues, 'elements')} id="dp-elements-issue" />

            {GROUPS.map((group) => {
              const rows = cat.dataElements.filter(group.holds)
              if (rows.length === 0) return null
              return (
                <div key={group.key} className="dp-group">
                  <div className="vd-inline">
                    <p className="label-block" id={'dp-group-' + group.key}>
                      {group.title}
                    </p>
                    <Toggletip label={'About ' + group.title.toLowerCase()}>{group.detail}</Toggletip>
                  </div>
                  <div className="checkcol" role="group" aria-labelledby={'dp-group-' + group.key}>
                    {rows.map((element) => {
                      const chosen = draft.elements[element.code]
                      const found = elementIssues(issues, element.code)
                      const id = 'dp-el-' + element.code
                      return (
                        <div key={element.code} className={element.sensitive ? 'check check-pii' : 'check'}>
                          <input
                            id={id}
                            type="checkbox"
                            checked={chosen !== undefined}
                            aria-describedby={found.length > 0 ? id + '-issue' : undefined}
                            onChange={(e) =>
                              setElement(element.code, e.target.checked ? { classification: '', note: '' } : null)
                            }
                          />
                          <div>
                            <span className="vd-inline">
                              <label htmlFor={id}>{element.name}</label>
                              <ReasonTip label={'Why ' + element.name + ' matters'} text={element.why} />
                            </span>
                            {chosen !== undefined && (
                              <>
                                <div className="field dp-level">
                                  <label htmlFor={id + '-level'}>Classification</label>
                                  <select
                                    id={id + '-level'}
                                    value={chosen.classification}
                                    aria-invalid={found.length > 0 ? true : undefined}
                                    aria-describedby={id + '-level-why' + (found.length > 0 ? ' ' + id + '-issue' : '')}
                                    onChange={(e) =>
                                      setElement(element.code, { ...chosen, classification: e.target.value })
                                    }
                                  >
                                    <option value="">Choose one</option>
                                    {classifications.map((c) => (
                                      <option key={c.code} value={c.code}>
                                        {valueLabel(c)}
                                      </option>
                                    ))}
                                  </select>
                                  <p className="field-note vd-inline" id={id + '-level-why'}>
                                    How exposed it is in this engagement
                                    <ReasonTip
                                      label="About this level"
                                      text={classifications.find((c) => c.code === chosen.classification)?.why}
                                    />
                                  </p>
                                </div>
                                <div className="field dp-level">
                                  <label htmlFor={id + '-note'}>Note</label>
                                  <input
                                    id={id + '-note'}
                                    type="text"
                                    value={chosen.note}
                                    aria-describedby={id + '-note-why'}
                                    onChange={(e) => setElement(element.code, { ...chosen, note: e.target.value })}
                                  />
                                </div>
                              </>
                            )}
                            <FieldIssues issues={found} id={id + '-issue'} />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </fieldset>

          {understated.length > 0 && (
            <div className="note warn">
              <strong>
                {sentenceList(understated.map((u) => u.name))} {understated.length === 1 ? 'is' : 'are'} classified
                below Restricted.
              </strong>
              <p className="vd-inline">
                It can be right. Confirm it and your name is recorded on the audit chain.
                <Toggletip label="Why this needs confirming">
                  The catalogue marks these sensitive, and everything built on this record follows the level you set: the reports, the assessments and the rules that read it.
                </Toggletip>
              </p>
              <div className="check-locked">
                <label htmlFor="dp-ack">
                  <input
                    id="dp-ack"
                    type="checkbox"
                    checked={acknowledged}
                    aria-describedby={'dp-ack-why' + (ackMissed ? ' dp-ack-issue' : '')}
                    onChange={(e) => {
                      setAcknowledged(e.target.checked)
                      if (e.target.checked) setAckMissed(false)
                    }}
                  />
                  I have read the reasons above and this classification is deliberate
                </label>
                <p className="field-locked" id="dp-ack-why">
                  Unticked again whenever the elements or their levels change
                </p>
                {ackMissed && (
                  <p className="field-error" id="dp-ack-issue" role="alert">
                    Tick this, or raise the classification. The record is refused until then.
                  </p>
                )}
              </div>
            </div>
          )}

          <fieldset className="block">
            <legend>How many, what for, how long</legend>
            <div className="vform">
              <div className="field wide">
                <label htmlFor="dp-band">Number of data subjects</label>
                <select
                  id="dp-band"
                  value={draft.subjectCountBand}
                  aria-invalid={issuesAt(issues, 'subjectCountBand').length > 0 ? true : undefined}
                  aria-describedby={
                    'dp-band-why' + (issuesAt(issues, 'subjectCountBand').length > 0 ? ' dp-band-issue' : '')
                  }
                  onChange={(e) => setDraft({ ...draft, subjectCountBand: e.target.value })}
                >
                  <option value="">Not stated</option>
                  {cat.subjectCountBands.map((b) => (
                    <option key={b.code} value={b.code}>
                      {valueLabel(b)}
                    </option>
                  ))}
                </select>
                <p className="field-note vd-inline" id="dp-band-why">
                  How many people, not how many records
                  <ReasonTip label="About this band" text={bandRow?.why} />
                </p>
                <FieldIssues issues={issuesAt(issues, 'subjectCountBand')} id="dp-band-issue" />
              </div>

              <div className="field full">
                <label htmlFor="dp-purpose">Purpose of processing</label>
                <textarea
                  id="dp-purpose"
                  rows={2}
                  value={draft.processingPurpose}
                  aria-describedby="dp-purpose-why"
                  onChange={(e) => setDraft({ ...draft, processingPurpose: e.target.value })}
                />
              </div>

              <div className="field">
                <label htmlFor="dp-retention">Retention period, in months</label>
                <input
                  id="dp-retention"
                  type="number"
                  min={0}
                  step={1}
                  value={draft.retentionPeriodMonths}
                  aria-invalid={issuesAt(issues, 'retentionPeriodMonths').length > 0 ? true : undefined}
                  aria-describedby={
                    'dp-retention-why' +
                    (issuesAt(issues, 'retentionPeriodMonths').length > 0 ? ' dp-retention-issue' : '')
                  }
                  onChange={(e) => setDraft({ ...draft, retentionPeriodMonths: e.target.value })}
                />
                <FieldIssues issues={issuesAt(issues, 'retentionPeriodMonths')} id="dp-retention-issue" />
              </div>

              <div className="field wide">
                <label htmlFor="dp-basis">Basis for that period</label>
                <input
                  id="dp-basis"
                  type="text"
                  value={draft.retentionBasis}
                  aria-describedby="dp-basis-why"
                  onChange={(e) => setDraft({ ...draft, retentionBasis: e.target.value })}
                />
                <p className="field-note" id="dp-basis-why">
                  The clause, the statute or the internal policy the period comes from.
                </p>
              </div>
            </div>
          </fieldset>

          <fieldset className="block">
            <legend>Where it sits, and where it goes</legend>
            <div className="vform">
              <div className="field">
                <label htmlFor="dp-storage">Storage country</label>
                <input
                  id="dp-storage"
                  type="text"
                  value={draft.storageCountry}
                  placeholder="IN"
                  maxLength={2}
                  aria-invalid={issuesAt(issues, 'storageCountry').length > 0 ? true : undefined}
                  aria-describedby={
                    'dp-storage-why' + (issuesAt(issues, 'storageCountry').length > 0 ? ' dp-storage-issue' : '')
                  }
                  onChange={(e) => setDraft({ ...draft, storageCountry: e.target.value.toUpperCase() })}
                />
                <FieldIssues issues={issuesAt(issues, 'storageCountry')} id="dp-storage-issue" />
              </div>

              <div className="field">
                <label htmlFor="dp-processing">Processing country</label>
                <input
                  id="dp-processing"
                  type="text"
                  value={draft.processingCountry}
                  placeholder="IN"
                  maxLength={2}
                  aria-invalid={issuesAt(issues, 'processingCountry').length > 0 ? true : undefined}
                  aria-describedby={
                    'dp-processing-why' +
                    (issuesAt(issues, 'processingCountry').length > 0 ? ' dp-processing-issue' : '')
                  }
                  onChange={(e) => setDraft({ ...draft, processingCountry: e.target.value.toUpperCase() })}
                />
                <FieldIssues issues={issuesAt(issues, 'processingCountry')} id="dp-processing-issue" />
              </div>

              <div className="field full">
                <label htmlFor="dp-transfer">Transfer countries</label>
                <div className="gstin-row">
                  <input
                    id="dp-transfer"
                    type="text"
                    value={transferDraft}
                    placeholder="AE"
                    maxLength={2}
                    aria-invalid={issuesAt(issues, 'transferCountries').length > 0 ? true : undefined}
                    aria-describedby={
                      'dp-transfer-why' + (issuesAt(issues, 'transferCountries').length > 0 ? ' dp-transfer-issue' : '')
                    }
                    onChange={(e) => setTransferDraft(e.target.value.toUpperCase())}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        addTransferCountry()
                      }
                    }}
                  />
                  <button type="button" onClick={addTransferCountry}>
                    Add country
                  </button>
                </div>
                <FieldIssues issues={issuesAt(issues, 'transferCountries')} id="dp-transfer-issue" />
                {draft.transferCountries.length > 0 && (
                  <div className="chiprow">
                    {draft.transferCountries.map((c) => (
                      <span key={c} className="cite">
                        {c}
                        <button
                          type="button"
                          className="linkish dp-chip-drop"
                          aria-label={'Remove ' + c}
                          onClick={() =>
                            setDraft({ ...draft, transferCountries: draft.transferCountries.filter((x) => x !== c) })
                          }
                        >
                          remove
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="field wide">
                <label htmlFor="dp-deletion">Deletion mechanism</label>
                <input
                  id="dp-deletion"
                  type="text"
                  value={draft.deletionMechanism}
                  aria-describedby="dp-deletion-why"
                  onChange={(e) => setDraft({ ...draft, deletionMechanism: e.target.value })}
                />
              </div>

              <div className="field wide">
                <label htmlFor="dp-evidence">Deletion evidence</label>
                <Picker
                  bare
                  id="dp-evidence"
                  label="Deletion evidence"
                  value={draft.deletionEvidenceDocumentId}
                  describedBy="dp-evidence-why"
                  options={documents.map((d) => ({ value: d.id, label: d.title || d.docType }))}
                  none="No document"
                  placeholder="Search the documents on file"
                  noun="document"
                  missing="A document not in this list"
                  onChange={(id) => setDraft({ ...draft, deletionEvidenceDocumentId: id })}
                />
                <p className="field-note" id="dp-evidence-why">
                  {documents.length === 0
                    ? 'No document on file yet. Upload one under Contracts and it appears here'
                    : 'A destruction certificate, or the clause that commits them to it'}
                </p>
              </div>
            </div>
          </fieldset>

          <CrossBorderPreview
            facts={{
              storageCountry: draft.storageCountry,
              processingCountry: draft.processingCountry,
              transferCountries: draft.transferCountries,
              sensitiveChosen,
              personalChosen,
            }}
            saved={false}
          />

          <div className="commit-bar">
            <button
              type="submit"
              className="primary"
              disabled={saving}
              data-action="tprm.vendor,vendor-record.a18"
            >
              {saving ? 'Saving' : 'Save this record'}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(false)
                setIssues([])
                setSaveError(null)
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </>
  )
}
